import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ActionHandler } from "@/components/UI/tools";
import { appState } from "@/app/appState";
import { client } from "@/api/client.gen";
import { Add_new, ArrowDown, ArrowRight } from "@/components/UI/svgs";
import { buildKeyTree, flattenKeyTree } from "@/app/workspace/settings/keyTree";
import { Pill, settingsRowId } from "@/app/workspace/settings/ui";
import {
    DEFAULT_PROMPT_SET_FIELDS,
    DefaultPromptSetField,
    emptyDefaultPromptSet,
    extractPlaceholders,
    i18nDependenciesForKey,
    instructionI18nKey,
    isCreateArtifactUserAction,
    isRichTextKey,
    matchRoute,
    parseDefaultPromptSetRecord,
    parseSkillRecord,
    parseSystemStateRecord,
    parseToolRecord,
    parseUserActionRecord,
    optionalPlaceholdersForKey,
    requiredPlaceholdersForKey,
    serializeDefaultPromptSetRecord,
    serializeSkillRecord,
    serializeSystemStateRecord,
    serializeToolRecord,
    serializeUserActionRecord,
    skillCommandI18nKey,
    skillGroupIdFromPath,
    skillIdFromKey,
    validateKvpEntry,
} from "./kvpRouteSpecs";
import { EXPECTED_KVP_KEYS, EXPECTED_KVP_KEY_SET } from "./kvpKeyRegistry";
import { KvpSideEffectsEditor, SettingsKeyLinks } from "./kvpSideEffectsEditor";
import { KvpEntryExplainer, KvpField, KvpGroupOverview } from "./kvpExplainer";
import { groupTitleForPath } from "./kvpDocs";

type KvpRow = { key: string; value: string };

const INDENT_PER_LEVEL = 14;

/** Empty editor value for a registry key that is not yet in the database. */
function emptyUnregisteredKvpValue(key: string): string {
    const spec = matchRoute(key);
    if (spec === "default_prompt_set") return serializeDefaultPromptSetRecord(emptyDefaultPromptSet());
    if (spec === "tool") return serializeToolRecord("");
    if (spec === "skill") {
        return serializeSkillRecord("", [], skillIdFromKey(key), "");
    }
    if (spec === "user_action") {
        return serializeUserActionRecord("", isCreateArtifactUserAction(key) ? "" : undefined);
    }
    if (spec === "system_state") return serializeSystemStateRecord("");
    return "";
}

function mergeExpectedKvp(db: KvpRow[]): { rows: KvpRow[]; persistedKeys: Set<string> } {
    // Union DB rows with kvpKeyRegistry. Stubs are local until Save.
    const persistedKeys = new Set(db.map((entry) => entry.key));
    const byKey = new Map(db.map((entry) => [entry.key, entry]));
    for (const key of EXPECTED_KVP_KEYS) {
        if (!byKey.has(key)) {
            byKey.set(key, { key, value: emptyUnregisteredKvpValue(key) });
        }
    }
    return {
        rows: [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key)),
        persistedKeys,
    };
}

export const useKvp = () => {
    const [rows, setRows] = useState<KvpRow[]>([]);
    const [persistedKeys, setPersistedKeys] = useState<Set<string>>(new Set());
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [originalKey, setOriginalKey] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isSuperAdmin = !!appState.user?.is_super_admin;

    const selected = selectedIndex != null ? rows[selectedIndex] : null;

    const loadKvp = async (preserveKey?: string | null) => {
        setLoading(true);
        setError(null);
        try {
            const response = await client.post({
                url: "/api/kvp/get-kvp",
                body: {},
                headers: { "Content-Type": "application/json" },
            });
            if (response.data?.entries) {
                const entries = response.data.entries as KvpRow[];
                const { rows: merged, persistedKeys: nextPersisted } = mergeExpectedKvp(entries);
                setRows(merged);
                setPersistedKeys(nextPersisted);
                const keep = preserveKey !== undefined ? preserveKey : originalKey;
                if (keep != null) {
                    const idx = merged.findIndex((e) => e.key === keep);
                    if (idx >= 0) {
                        setSelectedIndex(idx);
                        setOriginalKey(keep);
                    } else {
                        setSelectedIndex(null);
                        setOriginalKey(null);
                    }
                } else {
                    setSelectedIndex(null);
                    setOriginalKey(null);
                }
            } else {
                setError(
                    (response.error as { detail?: string })?.detail ??
                    appState.t("chat.requestFailed")
                );
            }
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : appState.t("chat.requestFailed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSuperAdmin) {
            loadKvp();
        }
    }, [isSuperAdmin]);

    const selectIndex = (index: number) => {
        setSelectedIndex(index);
        setOriginalKey(rows[index]?.key ?? null);
    };

    const selectKey = useCallback((key: string) => {
        const index = rows.findIndex((row) => row.key === key);
        if (index >= 0) {
            setSelectedIndex(index);
            setOriginalKey(rows[index]?.key ?? null);
        }
    }, [rows]);

    const clearSelection = useCallback(() => {
        setSelectedIndex(null);
        setOriginalKey(null);
    }, []);

    const updateSelected = (field: "key" | "value", text: string) => {
        if (selectedIndex == null) return;
        setRows((prev) =>
            prev.map((row, i) =>
                i === selectedIndex ? { ...row, [field]: text } : row
            )
        );
    };

    const addRow = () => {
        setRows((prev) => {
            const next = [...prev, { key: "", value: "" }];
            setSelectedIndex(next.length - 1);
            setOriginalKey("");
            return next;
        });
    };

    const deleteSelected = async (after?: () => void) => {
        if (selectedIndex == null || !selected) return;
        const keyToDelete = originalKey?.trim() || selected.key.trim();
        const isPersisted = !!(keyToDelete && persistedKeys.has(keyToDelete));
        const isExpected = EXPECTED_KVP_KEY_SET.has(keyToDelete);

        if (keyToDelete && !isPersisted && isExpected) return;

        if (isPersisted) {
            setSaving(true);
            try {
                const response = await client.post({
                    url: "/api/kvp/delete-kvp",
                    body: { key: keyToDelete },
                    headers: { "Content-Type": "application/json" },
                });
                if (response.error) {
                    appState.setModal({
                        title: appState.t("common.error"),
                        body:
                            (response.error as { detail?: string })?.detail ??
                            appState.t("common.delete"),
                    });
                    return;
                }
            } catch (e: unknown) {
                appState.setModal({
                    title: appState.t("common.error"),
                    body: e instanceof Error ? e.message : appState.t("common.delete"),
                });
                return;
            } finally {
                setSaving(false);
            }
            setSelectedIndex(null);
            setOriginalKey(null);
            after?.();
            await loadKvp(null);
            return;
        }

        setRows((prev) => prev.filter((_, i) => i !== selectedIndex));
        setSelectedIndex(null);
        setOriginalKey(null);
        after?.();
    };

    const saveSelected = async () => {
        if (!selected?.key.trim()) {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("settings.kvp.keyEmpty"),
            });
            return false;
        }

        const validation = validateKvpEntry(
            selected.key,
            selected.value,
            rows.map((row) => row.key),
        );
        if (!validation.valid) {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t(validation.errorKey ?? "common.error", validation.errorVars),
            });
            return false;
        }

        setSaving(true);
        try {
            if (
                originalKey &&
                originalKey.trim() &&
                originalKey !== selected.key &&
                persistedKeys.has(originalKey)
            ) {
                const del = await client.post({
                    url: "/api/kvp/delete-kvp",
                    body: { key: originalKey },
                    headers: { "Content-Type": "application/json" },
                });
                if (del.error) {
                    appState.setModal({
                        title: appState.t("common.error"),
                        body:
                            (del.error as { detail?: string })?.detail ??
                            appState.t("common.error"),
                    });
                    return false;
                }
            }

            const response = await client.post({
                url: "/api/kvp/set-kvp",
                body: { entries: [{ key: selected.key.trim(), value: selected.value }] },
                headers: { "Content-Type": "application/json" },
            });
            if (response.error) {
                appState.setModal({
                    title: appState.t("common.error"),
                    body:
                        (response.error as { detail?: string })?.detail ??
                        appState.t("common.save"),
                });
                return false;
            }
            await loadKvp(selected.key);
            return true;
        } catch (e: unknown) {
            appState.setModal({
                title: appState.t("common.error"),
                body: e instanceof Error ? e.message : appState.t("common.save"),
            });
            return false;
        } finally {
            setSaving(false);
        }
    };

    return {
        isSuperAdmin,
        rows,
        persistedKeys,
        selectedIndex,
        selected,
        originalKey,
        loading,
        saving,
        error,
        loadKvp,
        selectIndex,
        selectKey,
        clearSelection,
        updateSelected,
        addRow,
        deleteSelected,
        saveSelected,
    };
};

export type KvpState = ReturnType<typeof useKvp>;

export const KvpList = observer(({
    kvp,
    search,
    setSearch,
    expanded,
    toggleExpanded,
    onSelect,
    onSelectGroup,
    activePath,
    onAdd,
    onOpenI18n,
}: {
    kvp: KvpState;
    search: string;
    setSearch: (val: string) => void;
    expanded: Set<string>;
    toggleExpanded: (path: string) => void;
    onSelect: (key: string) => void;
    /** Clicking a branch (system_prompt, tool, ...) opens its overview. */
    onSelectGroup?: (path: string) => void;
    /** Path currently shown in the inspector: a key, or a branch with an overview. */
    activePath?: string | null;
    onAdd: () => void;
    onOpenI18n?: (key: string) => void;
}) => {
    const [missingOnly, setMissingOnly] = useState(false);
    const visibleKeys = useMemo(() => {
        const keys = kvp.rows.map((row) => row.key).filter(Boolean);
        if (!missingOnly) return keys;
        return keys.filter(
            (key) => EXPECTED_KVP_KEY_SET.has(key) && !kvp.persistedKeys.has(key),
        );
    }, [kvp.rows, kvp.persistedKeys, missingOnly]);
    const roots = useMemo(
        () => buildKeyTree(visibleKeys),
        [visibleKeys],
    );
    const rows = flattenKeyTree(roots, { expanded, search, expandAll: missingOnly });
    const hasDraft = !missingOnly && kvp.rows.some((row) => row.key === "");
    const selectedKey = kvp.selected?.key ?? null;
    const highlightedPath = selectedKey ?? activePath ?? null;

    return (
        <div className="flex flex-col gap-[.2rem] min-h-0">

            <label 
            onClick={(e) => {
                e.stopPropagation();
                setMissingOnly(!missingOnly);
            }}
            className={`filter-toggle px-[.85rem] pb-[.35rem] ${missingOnly ? "filter-toggle-active" : ""}`}>
                <span>
                    {appState.t("settings.i18n.missing")}
                </span>
            </label>
            <div className="px-[.35rem] pb-[.35rem] flex flex-row items-center gap-[.25rem]">
                <input
                    className="search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={appState.t("common.filterPlaceholder")}
                />
                <ActionHandler
                    title={appState.t("settings.kvp.add")}
                    funq={onAdd}
                    disabled={kvp.loading || kvp.saving}
                    className="btn-ghost"
                >
                    <Add_new size={14} />
                </ActionHandler>
            </div>
            
            {kvp.loading && rows.length === 0 && (
                <p className="tree-empty">{appState.t("common.loading")}</p>
            )}
            {kvp.error && (
                <p className="error-text px-[.5rem]">{kvp.error}</p>
            )}
            {!kvp.loading && rows.length === 0 && !hasDraft && (
                <p className="tree-empty">
                    {search.trim()
                        ? appState.t("settings.i18n.noMatch")
                        : appState.t("settings.kvp.empty")}
                </p>
            )}

            {hasDraft && (
                <div
                    onClick={() => onSelect("")}
                    className={`tree-node pl-[.5rem] ${selectedKey === "" ? "tree-node-selected" : ""}`}
                >
                    <span className="tree-node-label tree-node-label-mono tree-node-label-muted">
                        {appState.t("settings.i18n.newKey")}
                    </span>
                </div>
            )}

            {rows.map(({ node, depth, hasChildren }) => {
                const isSelected = node.path === highlightedPath;
                const humanName = groupTitleForPath(node.path);
                const unregistered = node.isKey
                    && EXPECTED_KVP_KEY_SET.has(node.path)
                    && !kvp.persistedKeys.has(node.path);
                const unsaved = node.isKey
                    && !kvp.persistedKeys.has(node.path)
                    && !EXPECTED_KVP_KEY_SET.has(node.path);
                const skillId = skillGroupIdFromPath(node.path);

                return (
                    <div
                        key={node.path}
                        id={settingsRowId("kvp", node.path)}
                        onClick={() => {
                            if (node.isKey) {
                                onSelect(node.path);
                            } else if (hasChildren) {
                                if (onSelectGroup) {
                                    if (!expanded.has(node.path)) toggleExpanded(node.path);
                                    onSelectGroup(node.path);
                                } else {
                                    toggleExpanded(node.path);
                                }
                            }
                        }}
                        style={{ paddingLeft: depth * INDENT_PER_LEVEL }}
                        className={`tree-node ${isSelected ? "tree-node-selected" : ""}`}
                    >
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasChildren) toggleExpanded(node.path);
                            }}
                            className={`tree-node-toggle ${hasChildren ? "" : "tree-node-toggle-empty"}`}
                        >
                            {expanded.has(node.path) ? <ArrowDown size={14} /> : <ArrowRight size={14} />}
                        </span>
                        <span
                            className={`tree-node-label ${node.isKey ? "tree-node-label-mono" : "tree-node-label-muted"} ${unregistered ? "tree-node-label-muted" : ""}`}
                            title={node.path}
                        >
                            {node.segment}{unsaved ? " *" : ""}
                            {humanName && (
                                <span className="tree-node-label-muted font-sans"> · {humanName}</span>
                            )}
                        </span>
                        {skillId && onOpenI18n && (
                            <Pill
                                tone="accent"
                                title={appState.t("settings.kvp.pill.i18nTitle")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenI18n(skillCommandI18nKey(skillId));
                                }}
                            >
                                {appState.t("settings.kvp.pill.i18n")}
                            </Pill>
                        )}
                    </div>
                );
            })}
        </div>
    );
});

const DEFAULT_PROMPT_SET_LABEL_KEYS: Record<DefaultPromptSetField, string> = {
    summariser: "settings.prompt.summariserLabel",
    synthesiser: "settings.prompt.synthesiserLabel",
    document_summariser: "settings.prompt.documentSummariserLabel",
    document_synthesiser: "settings.prompt.documentSynthesiserLabel",
};

const DEFAULT_PROMPT_SET_HINTS: Record<DefaultPromptSetField, string> = {
    summariser: "Transcriptions, step 1: compresses each chunk of the transcription.",
    synthesiser: "Transcriptions, step 2: merges the compressed chunks into the final summary.",
    document_summariser: "Documents, step 1: compresses each chunk of the document.",
    document_synthesiser: "Documents, step 2: merges the compressed chunks into the final summary.",
};

const PlaceholderChips = ({
    label,
    names,
    present,
    missingClass,
}: {
    label: string;
    names: string[];
    present: Set<string>;
    missingClass: "code-chip-danger" | "code-chip-muted";
}) => names.length > 0 ? (
    <div className="flex flex-col gap-[.2rem]">
        <small className="field-label">{label}</small>
        <div className="flex flex-row flex-wrap gap-[.25rem]">
            {names.map((name) => (
                <span
                    key={name}
                    className={`code-chip ${present.has(name) ? "code-chip-good" : missingClass}`}
                >
                    {`{${name}}`}
                </span>
            ))}
        </div>
    </div>
) : null;

export const KvpEditor = observer(({
    kvp,
    groupPath,
    onOpenKey,
    onOpenI18n,
    onEnsureI18n,
    onSaved,
    onDeleted,
}: {
    kvp: KvpState;
    /** A branch of the tree (system_prompt, tool, ...) to show an overview for when no entry is selected. */
    groupPath?: string | null;
    onOpenKey?: (key: string) => void;
    onOpenI18n?: (key: string) => void;
    onEnsureI18n?: (key: string) => Promise<void>;
    onSaved?: () => Promise<void> | void;
    onDeleted?: () => void;
}) => {
    const [activeField, setActiveField] = useState<DefaultPromptSetField | null>(null);
    const selected = kvp.selected;
    const spec = selected ? matchRoute(selected.key.trim()) : null;
    const skillId = selected ? skillIdFromKey(selected.key.trim()) : null;
    const richText = selected ? isRichTextKey(selected.key) : false;
    const skillRecord = spec === "skill" && selected ? parseSkillRecord(selected.value) : null;
    const userActionRecord = spec === "user_action" && selected
        ? parseUserActionRecord(selected.value)
        : null;
    const systemStateRecord = spec === "system_state" && selected
        ? parseSystemStateRecord(selected.value)
        : null;
    const toolRecord = spec === "tool" && selected ? parseToolRecord(selected.value) : null;
    const defaultPromptSet = spec === "default_prompt_set" && selected
        ? parseDefaultPromptSetRecord(selected.value)
        : null;
    const isCreateArtifact = selected ? isCreateArtifactUserAction(selected.key.trim()) : false;
    const helpKey = instructionI18nKey(spec);
    const required = selected ? requiredPlaceholdersForKey(selected.key.trim()) : [];
    const optional = selected ? optionalPlaceholdersForKey(selected.key.trim()) : [];
    const deletedRequired = selected && spec === "user_action" && isCreateArtifact
        ? requiredPlaceholdersForKey(selected.key.trim(), "deleted")
        : [];
    const present = skillRecord
        ? extractPlaceholders(skillRecord.prompt)
        : userActionRecord
            ? extractPlaceholders(userActionRecord.prompt)
            : systemStateRecord
                ? extractPlaceholders(systemStateRecord.prompt)
                : selected
                    ? extractPlaceholders(selected.value)
                    : new Set<string>();
    const deletedPresent = userActionRecord?.deleted != null
        ? extractPlaceholders(userActionRecord.deleted)
        : new Set<string>();
    const i18nKeys = selected
        ? i18nDependenciesForKey(selected.key.trim(), selected.value)
        : [];
    const validation = selected
        ? validateKvpEntry(selected.key, selected.value, kvp.rows.map((row) => row.key))
        : { valid: true };
    const selectedPersistedKey = kvp.originalKey?.trim() || selected?.key.trim() || "";
    const canDelete = !!selected && (
        !selectedPersistedKey
        || kvp.persistedKeys.has(selectedPersistedKey)
        || !EXPECTED_KVP_KEY_SET.has(selectedPersistedKey)
    );

    useEffect(() => {
        if (!selected) return;
        if (spec === "skill" && skillRecord) {
            const next = serializeSkillRecord(
                skillRecord.prompt,
                skillRecord.side_effects,
                skillId,
                skillRecord.use_case,
            );
            if (next !== selected.value) kvp.updateSelected("value", next);
            return;
        }
        if (spec === "user_action" && userActionRecord) {
            const deleted = isCreateArtifact
                ? (userActionRecord.deleted ?? "")
                : userActionRecord.deleted;
            const next = serializeUserActionRecord(userActionRecord.prompt, deleted);
            if (next !== selected.value) kvp.updateSelected("value", next);
            return;
        }
        if (spec === "system_state" && systemStateRecord) {
            const next = serializeSystemStateRecord(systemStateRecord.prompt);
            if (next !== selected.value) kvp.updateSelected("value", next);
            return;
        }
        if (spec === "default_prompt_set" && defaultPromptSet) {
            const next = serializeDefaultPromptSetRecord(defaultPromptSet);
            if (next !== selected.value) kvp.updateSelected("value", next);
            return;
        }
        if (spec === "tool" && toolRecord) {
            const next = serializeToolRecord(toolRecord.description);
            if (next !== selected.value) kvp.updateSelected("value", next);
        }
    }, [selected, spec, skillRecord, userActionRecord, systemStateRecord, defaultPromptSet, toolRecord, skillId, isCreateArtifact, kvp]);

    const groupOverview = !selected && groupPath
        ? (
            <KvpGroupOverview
                path={groupPath}
                rows={kvp.rows}
                persistedKeys={kvp.persistedKeys}
                onOpenKey={(key) => onOpenKey?.(key)}
            />
        )
        : null;

    return (
        <div className="flex flex-col gap-2 w-full min-h-[24rem]">
            <div className="flex flex-row items-center justify-between gap-1">
                <div className="inspector-header">
                    <p className="eyebrow">{appState.t("settings.kvp")}</p>
                    <p className="h2 truncate">{selected?.key.trim() || "Key-value pairs"}</p>
                </div>
                <ActionHandler
                    title={appState.t("settings.kvp.reload")}
                    funq={() => kvp.loadKvp()}
                    disabled={kvp.loading || kvp.saving}
                >
                    {appState.t("settings.kvp.reload")}
                </ActionHandler>
            </div>

            {kvp.loading && (
                <p className="detail-text">{appState.t("common.loading")}</p>
            )}
            {kvp.error && (
                <p className="error-text">{kvp.error}</p>
            )}

            {selected ? (
                <div key={(kvp.originalKey ?? selected.key) || "draft"} className="flex flex-col gap-2">
                    <KvpEntryExplainer keyPath={selected.key.trim()} field={activeField} />
                    <label className="field">
                        <small className="field-label">{appState.t("settings.kvp.key")}</small>
                        <input
                            className="input input-mono"
                            value={selected.key}
                            onChange={(e) => kvp.updateSelected("key", e.target.value)}
                            placeholder={appState.t("settings.kvp.keyPlaceholder")}
                        />
                    </label>
                    {helpKey && (
                        <p className="help-text">{appState.t(helpKey)}</p>
                    )}
                    <SettingsKeyLinks
                        label={appState.t("settings.kvp.i18nLinks")}
                        keys={i18nKeys}
                        onOpen={onOpenI18n}
                    />
                    <PlaceholderChips
                        label={appState.t("settings.kvp.requiredParams")}
                        names={required}
                        present={present}
                        missingClass="code-chip-danger"
                    />
                    <PlaceholderChips
                        label={appState.t("settings.kvp.optionalParams")}
                        names={optional}
                        present={present}
                        missingClass="code-chip-muted"
                    />
                    {!validation.valid && (
                        <p className="error-text">
                            {appState.t(validation.errorKey ?? "common.error", validation.errorVars)}
                        </p>
                    )}
                    {spec === "default_prompt_set" && defaultPromptSet ? (
                        <>
                            {DEFAULT_PROMPT_SET_FIELDS.map((field: DefaultPromptSetField) => (
                                <KvpField
                                    key={field}
                                    label={appState.t(DEFAULT_PROMPT_SET_LABEL_KEYS[field])}
                                    hint={DEFAULT_PROMPT_SET_HINTS[field]}
                                    value={defaultPromptSet[field]}
                                    richText={richText}
                                    onFocus={() => setActiveField(field)}
                                    onChange={(text) => kvp.updateSelected(
                                        "value",
                                        serializeDefaultPromptSetRecord({ ...defaultPromptSet, [field]: text }),
                                    )}
                                />
                            ))}
                        </>
                    ) : spec === "skill" && skillRecord ? (
                        <>
                            <label className="field">
                                <small className="field-label">{appState.t("settings.kvp.skill.useCase")}</small>
                                <input
                                    className="input input-mono"
                                    value={skillRecord.use_case}
                                    onChange={(e) => kvp.updateSelected(
                                        "value",
                                        serializeSkillRecord(
                                            skillRecord.prompt,
                                            skillRecord.side_effects,
                                            skillId,
                                            e.target.value,
                                        ),
                                    )}
                                />
                            </label>
                            <KvpField
                                label={appState.t("settings.kvp.value")}
                                value={skillRecord.prompt}
                                richText={richText}
                                onChange={(text) => kvp.updateSelected(
                                    "value",
                                    serializeSkillRecord(
                                        text,
                                        skillRecord.side_effects,
                                        skillId,
                                        skillRecord.use_case,
                                    ),
                                )}
                            />
                            <KvpSideEffectsEditor
                                skillId={skillId}
                                specs={skillRecord.side_effects}
                                usedInPrompt={present}
                                onChange={(specs) => kvp.updateSelected(
                                    "value",
                                    serializeSkillRecord(
                                        skillRecord.prompt,
                                        specs,
                                        skillId,
                                        skillRecord.use_case,
                                    ),
                                )}
                                onOpenI18n={onOpenI18n}
                                onEnsureI18n={onEnsureI18n}
                            />
                        </>
                    ) : spec === "system_state" && systemStateRecord ? (
                        <KvpField
                            label={appState.t("settings.kvp.value")}
                            value={systemStateRecord.prompt}
                            richText={richText}
                            onChange={(text) => kvp.updateSelected(
                                "value",
                                serializeSystemStateRecord(text),
                            )}
                        />
                    ) : spec === "user_action" && userActionRecord ? (
                        <>
                            <KvpField
                                label={appState.t("settings.kvp.value")}
                                value={userActionRecord.prompt}
                                richText={richText}
                                onChange={(text) => kvp.updateSelected(
                                    "value",
                                    serializeUserActionRecord(
                                        text,
                                        isCreateArtifact
                                            ? (userActionRecord.deleted ?? "")
                                            : userActionRecord.deleted,
                                    ),
                                )}
                            />
                            {isCreateArtifact && (
                                <>
                                    <PlaceholderChips
                                        label={appState.t("settings.kvp.userAction.deleted")}
                                        names={deletedRequired}
                                        present={deletedPresent}
                                        missingClass="code-chip-danger"
                                    />
                                    <KvpField
                                        label={appState.t("settings.kvp.userAction.deleted")}
                                        value={userActionRecord.deleted ?? ""}
                                        richText={richText}
                                        minHeightRem={12}
                                        onChange={(text) => kvp.updateSelected(
                                            "value",
                                            serializeUserActionRecord(userActionRecord.prompt, text),
                                        )}
                                    />
                                </>
                            )}
                        </>
                    ) : spec === "tool" && toolRecord ? (
                        <KvpField
                            label={appState.t("settings.kvp.tool.description")}
                            value={toolRecord.description}
                            richText={richText}
                            onChange={(text) => kvp.updateSelected(
                                "value",
                                serializeToolRecord(text),
                            )}
                        />
                    ) : (
                        <KvpField
                            label={appState.t("settings.kvp.value")}
                            value={selected.value}
                            richText={richText}
                            onChange={(text) => kvp.updateSelected("value", text)}
                        />
                    )}
                    <div className="flex flex-row gap-[.25rem] shrink-0">
                        <ActionHandler
                            title={appState.t("common.save")}
                            funq={async () => {
                                const ok = await kvp.saveSelected();
                                if (ok) await onSaved?.();
                            }}
                            disabled={kvp.loading || kvp.saving || !validation.valid}
                        >
                            {kvp.saving ? appState.t("common.saving") : appState.t("common.save")}
                        </ActionHandler>
                        <ActionHandler
                            title={appState.t("common.delete")}
                            funq={() => kvp.deleteSelected(onDeleted)}
                            disabled={kvp.loading || kvp.saving || !canDelete}
                            className="btn-danger"
                        >
                            {appState.t("common.delete")}
                        </ActionHandler>
                    </div>
                </div>
            ) : groupOverview ?? (
                <p className="inspector-empty-text">{appState.t("settings.i18n.selectKey")}</p>
            )}
        </div>
    );
});
